import { useEffect, useState, createContext, useContext, useCallback } from "react";
import { auth as backendAuth } from "../lib/backend/auth";
import { functions as backendFunctions } from "../lib/backend/functions";

const AuthContext = createContext(null);

const OAUTH_REDIRECT_URL =
  process.env.REACT_APP_OAUTH_REDIRECT_URL || window.location.origin;

// Best-effort link of orphan guest tickets to the freshly-authenticated
// user. Fire-and-forget: a network failure here must NOT block sign-in.
// The next sign-in will retry. Spec: issue #94 §7 — only run when the
// session's email is verified. We treat the verification flag as
// "verified-by-default when absent" because the JWT was already issued
// by the auth server (which gates session creation behind verification
// in every Pikra signup flow). The edge function performs the same
// check server-side — this client guard just saves a round trip in the
// rare case the SDK exposes `email_verified: false` explicitly.
async function claimGuestTicketsBestEffort(user) {
  if (!user || !user.id) return null;
  if (user.email_verified === false) return null;
  try {
    const { data, error } = await backendFunctions.claimGuestTickets();
    if (error) return null;
    return data;
  } catch {
    return null;
  }
}

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [profile, setProfile] = useState(null);
  const [loading, setLoading] = useState(true);

  const fetchProfile = useCallback(async (currentUser) => {
    const { data } = await backendAuth.getProfile(currentUser.id);

    if (data) {
      setProfile(data);
    } else if (currentUser) {
      // Profile doesn't exist yet (e.g. new Google OAuth user) — create one
      const suffix = Math.random().toString(36).slice(2, 6);
      const fallbackUsername =
        currentUser.name ||
        `${currentUser.email?.split("@")[0]}_${suffix}` ||
        "usuario";

      const { data: newProfile } = await backendAuth.setProfile({
        name: fallbackUsername,
      });

      setProfile(newProfile);
    }
  }, []);

  const checkUser = useCallback(async () => {
    const { data, error } = await backendAuth.getCurrentUser();
    if (!error && data?.user) {
      setUser(data.user);
      await fetchProfile(data.user);
      // Existing session loaded — try to claim any orphan tickets that
      // arrived since the last sign-in (e.g. user bought as guest from a
      // different device, then returned to this one).
      claimGuestTicketsBestEffort(data.user);
    } else {
      setUser(null);
      setProfile(null);
    }
    setLoading(false);
  }, [fetchProfile]);

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const hasOAuthCallback = params.has("insforge_code");

    if (hasOAuthCallback) {
      // The SDK auto-detects insforge_code and exchanges it for a session,
      // but we need to wait for that exchange to complete before checking user.
      // Poll getCurrentUser until the session is established or timeout.
      let attempts = 0;
      const maxAttempts = 20; // 20 * 250ms = 5s max wait
      const poll = async () => {
        const { data, error } = await backendAuth.getCurrentUser();
        if (!error && data?.user) {
          setUser(data.user);
          await fetchProfile(data.user);
          // OAuth completion: the provider already verified the email,
          // so claim any pre-existing guest tickets bought with it.
          claimGuestTicketsBestEffort(data.user);
          setLoading(false);
          // Clean up the URL (remove insforge params)
          const url = new URL(window.location.href);
          url.searchParams.delete("insforge_code");
          url.searchParams.delete("insforge_state");
          window.history.replaceState({}, "", url.pathname + url.search);
        } else if (++attempts < maxAttempts) {
          setTimeout(poll, 250);
        } else {
          // Timed out — fall back to normal check
          setUser(null);
          setProfile(null);
          setLoading(false);
        }
      };
      // Small initial delay to give the SDK time to start processing
      setTimeout(poll, 300);
    } else {
      checkUser();
    }
  }, [checkUser, fetchProfile]);

  const signUp = async (email, password, username) => {
    const { data, error } = await backendAuth.signUp({
      email,
      password,
      name: username,
    });
    if (error) throw error;

    if (data?.requireEmailVerification) {
      // Return info so the caller can handle verification flow
      return data;
    }

    // No verification required — user is signed in
    if (data?.user) {
      setUser(data.user);
      // Set profile with the chosen username
      const { data: profileData } = await backendAuth.setProfile({
        name: username,
      });
      setProfile(profileData);
      // Fresh signup with no verification gate: the email is implicitly
      // trusted by the backend, so claim any guest tickets bought with it.
      claimGuestTicketsBestEffort(data.user);
    }
    return data;
  };

  const signIn = async (email, password) => {
    const { data, error } = await backendAuth.signInWithPassword({
      email,
      password,
    });
    if (error) throw error;
    if (data?.user) {
      setUser(data.user);
      await fetchProfile(data.user);
      // After a successful password sign-in, link any orphan tickets the
      // user purchased as guest before they had an account.
      claimGuestTicketsBestEffort(data.user);
    }
    return data;
  };

  const signInWithOAuth = async (provider) => {
    const { error } = await backendAuth.signInWithOAuth({
      provider,
      redirectTo: OAUTH_REDIRECT_URL,
    });
    if (error) throw error;
  };

  const updateProfile = async (fields) => {
    const { data, error } = await backendAuth.setProfile(fields);
    if (error) throw error;
    if (data) setProfile(data);
    return data;
  };

  const signOut = async () => {
    await backendAuth.signOut();
    setUser(null);
    setProfile(null);
  };

  return (
    <AuthContext.Provider
      value={{ user, profile, loading, signUp, signIn, signInWithOAuth, signOut, checkUser, updateProfile }}
    >
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error("useAuth must be used within AuthProvider");
  return ctx;
}
